import {
  type ReplayTextField as ReplayTextFieldSpec,
  type ReplayTextProblem,
  problemSentence,
} from '@/lib/replay-settings';
import { cn } from '@/lib/utils';
import { useId } from 'react';

/**
 * One text of the instruction (docs/specs/rejeu.md §10.3): its label, its
 * « n / max caractères » counter, the line that says when it is served, and the
 * field.
 *
 * ## A bound that is SHOWN, not enforced by the field
 *
 * No `maxLength`: the other texts of Réglages cut a paste at the bound, but here
 * the refusal is part of the screen (« Refus de validation rendus en ligne »).
 * A field that silently dropped the tail of a pasted instruction would hide the
 * very thing the operator has to decide - which paragraph goes - so the text
 * stays whole, the counter goes red and the sentence under the field says why
 * the save is closed.
 *
 * ## The colour says what moved
 *
 * A text that differs from the one in force wears the yellow of the planche
 * (`jaune-deep` border on `jaune-tint`), the same signal as the box under the
 * fields. Every state keeps the 2 px border, so typing the first letter does not
 * move the page by a pixel.
 */

export interface ReplayTextFieldProps {
  readonly field: ReplayTextFieldSpec;
  readonly value: string;
  /** The text differs from the one in force. */
  readonly touched: boolean;
  /** What the server would refuse in it, or null. */
  readonly problem: ReplayTextProblem | null;
  readonly disabled: boolean;
  readonly onChange: (value: string) => void;
}

export function ReplayTextField({
  field,
  value,
  touched,
  problem,
  disabled,
  onChange,
}: ReplayTextFieldProps) {
  const id = useId();
  const hintId = `${id}-aide`;
  const problemId = `${id}-refus`;
  const over = problem === 'too-long';

  return (
    <div className="flex min-w-0 flex-col gap-1.5">
      <div className="flex items-baseline justify-between gap-3">
        <label htmlFor={id} className="font-bold text-base text-gray-900">
          {field.label}
        </label>
        <span
          data-testid={`rejeu-compteur-${field.key}`}
          className={cn(
            'text-caption tabular-nums',
            over ? 'font-semibold text-rouge-deep' : 'text-gray-600',
          )}
        >
          {value.length} / {field.max} caractères
        </span>
      </div>
      <p id={hintId} className="text-gray-600 text-sm">
        {field.hint}
      </p>
      <textarea
        id={id}
        data-testid={`rejeu-texte-${field.key}`}
        data-touched={touched ? 'true' : 'false'}
        value={value}
        rows={field.rows}
        disabled={disabled}
        aria-describedby={problem === null ? hintId : `${hintId} ${problemId}`}
        aria-invalid={problem === null ? undefined : true}
        onChange={(event) => onChange(event.currentTarget.value)}
        className={cn(
          'w-full resize-y rounded-md border-2 p-3 text-gray-800 text-sm leading-relaxed focus-visible:border-bleu-nuit focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
          problem !== null
            ? 'border-rouge-deep bg-white'
            : touched
              ? 'border-jaune-deep bg-jaune-tint'
              : 'border-gray-200 bg-white',
        )}
      />
      {problem === null ? null : (
        <p
          id={problemId}
          role="alert"
          data-testid={`rejeu-refus-${field.key}`}
          className="text-rouge-deep text-sm"
        >
          {problemSentence(field.key, problem)}
        </p>
      )}
    </div>
  );
}
